import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { Box, Modal } from "@mui/material";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import ModalLayout from "../FormModel/ModalLayout";
import { configState } from "../../../../recoil/atoms";
import InventoryModal from "../FormModel/InventoryModal";
import LinearProgress from "@mui/material/LinearProgress";
import { IconPencil, IconPackage, IconSwitch } from "@tabler/icons";
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import ToggleStatusDialog from "../../../../utils/FormFields/ToggleStatusDialog";
import useAuthorization from "../../../Authentication/permissions/Authorization";
import { Button, Grid, Card, CardContent, Typography, Switch } from "@mui/material";
import { InventoryApi, GetAllInventoriesResponseInner } from "../../../../api/medical-operations";

const commonCardStyle = {
  border: '1px solid #ccc',
  borderRadius: 3,    
  mt: 2,
  width: "30%",

  '@media (max-width: 768px)': {
    width: "100%",
  },
};

export default function Inventories() {
  const navigate = useNavigate();  
  const config = useRecoilValue(configState);
  
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [inventoryID, setInventoryID] = useState<string>("");
  const [openModal, setOpenModal] = useState<boolean>(false);  
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [editModal, setEditModal] = useState<GetAllInventoriesResponseInner>();
  const [switchStates, setSwitchStates] = useState<Record<string, boolean>>({});
  const [inventories, setInventories] = useState<GetAllInventoriesResponseInner[]>([]);

  const { canEditInventory, canDeactivateInventory } = useAuthorization();

  const handleOpenModal = (inventory: any) => {
    setOpenModal(true);
    setEditModal(inventory);
  }

  const handleCloseModal = () => {
    setOpenModal(false);    
    setEditModal(undefined);
    getInventories();
  };

  const getInventories = () => {
    const Inventories = new InventoryApi(config);
    Inventories.getAllInventories()
      .then((res) => {
        setIsLoading(false);        
        setInventories(res.data);              
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.detail);
      });
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDeactivate = (id: string) => {
    setInventoryID(id);
    handleOpenDialog();
  };

  const handleConfirm = () => {
    handleInActive(inventoryID);
    handleCloseDialog();
    setSwitchStates((prevSwitchStates) => ({
      ...prevSwitchStates,
      [inventoryID]: false,
    }));
  };

  const handleInActive = (inventoryID: any) => {
    const inventory = new InventoryApi(config);
    inventory.toggleInventoryStatus(inventoryID)
      .then((res) => {
        getInventories(); 
        toast.success(res.data.Message!);        
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  useEffect(() => {
    getInventories();    
  }, []);  

  useEffect(() => {
    const initialSwitchStates = inventories.reduce((acc, inventory) => {
      const inventoryID = inventory.InventoryID;
      if (inventoryID) {
        acc[inventoryID] = true;
      }
      return acc;
    }, {} as Record<string, boolean>);
    setSwitchStates(initialSwitchStates);
  }, [inventories]);

  return (      
    <MainCard>
      <Grid>
        {!isLoading ? (
          <>
          <Box display="flex" justifyContent="flex-end">              
            <Button
              variant="contained"
              color="secondary"
              startIcon={< IconSwitch/>}
              onClick={()=>{
                navigate("/deactivated-inventories")
              }}
            >
              Deactivated Inventories                    
            </Button>              
          </Box>        
          {inventories.length > 0 ? (
            <>
            <Modal
              open={openModal}
              onClose={handleCloseModal}
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <Box>
                <ModalLayout>
                  <InventoryModal handleCloseModal={handleCloseModal} editInventory={editModal} />
                </ModalLayout>
              </Box>
            </Modal>                              
            <Box style={{ display: 'flex', flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
              {inventories.map((inventory: any) => (
                <Card key={inventory.InventoryID} sx={commonCardStyle}>
                  <CardContent sx={{ padding: 0 }}>
                    <Box display="flex" justifyContent="flex-end" sx={{ pb: 1, pr: 2, pt: 2 }} style={{ backgroundColor: '#f0e6f4' }}>
                      {canDeactivateInventory && (
                        <Switch defaultChecked 
                          checked={switchStates[inventory.InventoryID] || false}
                          color="secondary"
                          onClick={() => {                        
                            handleDeactivate(inventory.InventoryID)
                          }}
                        />
                      )}                      
                    </Box>
                    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }} sx={{ mt: 3, ml: 4 }}>                          
                      <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 4 }}>
                        <Typography 
                        variant="h4" 
                        component="div" 
                        style={{ cursor: 'pointer' }} 
                        sx={{ 
                          "&:hover": {                             
                            textDecoration: "underline"
                          } 
                        }}
                        onClick={() =>{
                          navigate(`/inventory/${inventory.InventoryID}`,{
                            state: inventory,
                          })
                        }}>
                          {inventory.InventoryName}
                        </Typography>  
                        <Typography 
                          variant="body2" 
                          component="div" 
                          color="textSecondary"                            
                        >
                          {inventory.CollectionsNumber} Collections
                        </Typography> 
                      </Box>                   
                      {canEditInventory && (
                        <Box>
                          <Button
                            startIcon={<IconPencil />}
                            onClick={() => handleOpenModal(inventory)}
                            color="secondary"
                          />                        
                        </Box>  
                      )}                    
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>  
            <ToggleStatusDialog
              name={"Inventory"}
              handleCloseDialog={handleCloseDialog}
              handleConfirm={handleConfirm}
              openDialog={openDialog}
              activating={false}
              deactivating={true} 
            />             
            </>
            ):(
              <Box textAlign="center">
                <IconPackage size={100}/>
                <Typography variant="body2" color="textSecondary">
                  Seems like there are no inventories here. Let's kick things off by creating one!
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Head over to the Inventory Template section and pick a Template to craft your new Inventory.
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <LinearProgress color="secondary" />
        )}
      </Grid>
    </MainCard>    
  );
}
